import React, { useEffect, useState, useRef, useMemo } from "react";
import { withRouter } from "react-router-dom";
import { matchRoutes } from "react-router-config";
import { connect } from "react-redux";

import { asideRepositoryNavigationByModule } from "../Repository/navigation";

import styles from "./App.module.sass";

import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    uploadAction,
    downloadAction,
    toggleMenu,
    setLocale,
    auditNoteSubmit,
    removeModal,
    setDataFileStatusAction,
    deleteFilePermanentlyAction,
    deleteInvalidFilePermanentlyAction,
    hideFileAction,
    downloadFileAction,
    reprocessRepositoryAction,
    genericSearchAction,
    getFindingDetails,
    reprocessFileAction,
    getDownloadTemplateData,
} from "../../store/actions";

import proxy from "../../routes/proxy";

import { changeDeepObject, hydrateRoute } from "../../utils";
import { redirectNotValid, buildStaticLinks, applyThemeWithPriority } from "../../utils/utils";
import { cleanPropertiesNestedInMenu } from "../../utils/mapPropertiesForMenu";
import useUpdateThemes from "../../hooks/useUpdateThemes";

import {
    appRedirectValidations,
    controllerQueries,
    generateChildren,
    getQueryResultsArray,
    onClickConfiguratorApp,
    isPageLoading,
    GlobalPageLayout,
    useControllerInitialization,
    organizationAndProjectSelection,
    buildGenericMenu,
    buildDisplayComponents,
    buildHeaderComponents,
    headerTitle,
    getDownloadableQueries,
    generateNavigation,
    buildNavProps,
    headerFileTypesPresent,
} from "../utils/utils";

const AppNewLogic = (props) => {
    const isInitialMount = useRef(true);
    const lastAppliedThemeKeyRef = useRef(null);

    const {
        pushModal,
        modules,
        fetch,
        match,
        route,
        location,
        history,
        reload,
        matchParams,
        projectId,
        modulePrefix,
        locale,
        fetchResults,
        fetchStatus,
        appContext,
        auth,
        userInformation,
        i18n,
        hasHeroBanner,
        hideNewMenu
    } = useControllerInitialization(props);

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const initialViewMode = searchParams.get("viewMode") || "source" || null;
    const [viewMode, setViewMode] = useState(initialViewMode);
    const previousViewMode = useRef(viewMode);
    let { fiscalYear, importId, section, fileType, entity } = matchParams;

    const previousFiscalYear = useRef(fiscalYear);
    const previousImportId = useRef(matchParams.importId);

    const {
        menuOrganizationProject,
        organizationProjects,
        projectFiscalYears,
        project,
        fiscalYears,
        fiscalYearsWithImportIds,
        importIdsFromUrlFiscalYear,
        fiscalYear: finalFiscalYear,
        importId: finalImportId,
        queryStatus: organizationProjectQueryStatus,
    } = organizationAndProjectSelection({
        fetchResults,
        fetchStatus,
        component: AppNewLogic,
        matchParams,
        props,
        i18n,
        locale,
        fiscalYear,
        importId,
        projectId,
        viewMode,
    });

    const { navData, importIdToUse, routeQueries, params, firstModuleFromProject } =
        buildGenericMenu(
            modules,
            fetchResults,
            locale,
            fiscalYear,
            projectId,
            importId,
            project,
            location,
            matchParams,
            route,
            searchParams.get("viewMode") || "source",
        );

    const { hasSource, hasLegal } = headerFileTypesPresent(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        {},
        i18n,
        locale,
        fiscalYear,
        importId,
        projectId
    );

    useEffect(() => {
        if (searchParams.has("viewMode")) {
            setViewMode(searchParams.get("viewMode"));
        } else {
            setViewMode(!hasSource && hasLegal ? "legal" : "source");
        }
    }, [searchParams, hasSource, hasLegal]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            const initialPreferredViewMode = !hasSource && hasLegal ? "legal" : viewMode;
            previousViewMode.current = initialPreferredViewMode;

            if (initialPreferredViewMode && !searchParams.get("viewMode")) {
                const currentSearchParams = new URLSearchParams(location.search);
                currentSearchParams.set("viewMode", initialPreferredViewMode);
                const newSearch = currentSearchParams.toString();
                const pathnameWithFull = hydrateRoute(match.path, {
                    ...matchParams,
                    importId: "full",
                });
                const newUrl = `${pathnameWithFull}?${newSearch}`;
                if (`${location.pathname}${location.search || ""}` !== newUrl) {
                    history.replace(newUrl);
                }
            }
            return;
        }

        if (viewMode && previousViewMode.current !== viewMode && history && location) {
            previousViewMode.current = viewMode;

            const currentSearchParams = new URLSearchParams(location.search);
            currentSearchParams.set("viewMode", !hasSource && hasLegal ? "legal" : viewMode);
            const newSearch = currentSearchParams.toString();
            const pathnameWithFull = hydrateRoute(match.path, {
                ...matchParams,
                importId: "full",
            });
            const newUrl = `${pathnameWithFull}?${newSearch}`;
            if (`${location.pathname}${location.search || ""}` !== newUrl) {
                history.replace(newUrl);
            }
        }
    }, [
        viewMode,
        location,
        history,
        searchParams,
        fiscalYear,
        projectId,
        modulePrefix,
        locale,
        entity,
        hasSource,
        hasLegal,
        match.path,
        matchParams,
    ]);

    useEffect(() => {
        if (isInitialMount.current) {
            previousFiscalYear.current = matchParams.fiscalYear;
            previousImportId.current = matchParams.importId;
            return;
        }

        const fiscalYearChanged = previousFiscalYear.current !== matchParams.fiscalYear;
        const importIdChanged = previousImportId.current !== matchParams.importId;

        if ((fiscalYearChanged || importIdChanged) && reload) {
            previousFiscalYear.current = matchParams.fiscalYear;
            previousImportId.current = matchParams.importId;

            reload();
        }
    }, [matchParams.fiscalYear, matchParams.importId, reload]);

    const { loading = true, success = false } = organizationProjectQueryStatus || {};
    const { updateThemes, LoadingComponent } = useUpdateThemes(loading, success);
    useEffect(() => {
        if (
            loading ||
            !success ||
            !organizationProjects ||
            !Object.keys(organizationProjects).length
        ) {
            return;
        }

        const currentProject =
            Array.isArray(organizationProjects.projects) && projectId
                ? organizationProjects.projects.find((project) => project.id === projectId)
                : null;
        const projectTheme =
            currentProject && Object.keys(currentProject?.mosaicThemeConfig || {}).length
                ? currentProject.mosaicThemeConfig
                : null;
        const organizationTheme =
            organizationProjects?.mosaicThemeConfig &&
                Object.keys(organizationProjects?.mosaicThemeConfig || {}).length
                ? organizationProjects.mosaicThemeConfig
                : null;
        const selectedTheme = projectTheme || organizationTheme || null;
        const nextThemeKey = JSON.stringify(selectedTheme || {});

        if (lastAppliedThemeKeyRef.current === nextThemeKey) {
            return;
        }
        lastAppliedThemeKeyRef.current = nextThemeKey;
        applyThemeWithPriority(organizationProjects, projectId, updateThemes);
    }, [organizationProjects, projectId, updateThemes, loading, success]);

    fiscalYear = finalFiscalYear ?? fiscalYear;
    importId = finalImportId ?? importId;

    const {
        repositoryStatus = { status: 1 },
        getTotalExportLines = "",
        genericOverview,
    } = getQueryResultsArray(fetchResults, [
        {
            resultName: "repositoryStatus",
            queryName: `${locale}_repository_status_fiscalYear_${fiscalYear}_projectId_${projectId}${viewMode ? `_viewMode_${viewMode}` : ""}`,
        },
        {
            resultName: "getTotalExportLines",
            queryName: `${locale}_get_limit_export`,
        },
        {
            resultName: "genericOverview",
            queryName: `${locale}_generic_overview_fiscalYear_${fiscalYear}_importId_${importId}_locale_${locale}${modulePrefix ? `_modulePrefix_${modulePrefix}` : ""}_projectId_${projectId}${viewMode ? `_viewMode_${viewMode}` : ""}`,
        },
    ]);

    const getLayout = JSON.parse(genericOverview || "[]");
    changeDeepObject(getLayout, ["component", "cards"], {
        SubmitSalesInfo: { projectData: projectFiscalYears, viewMode: viewMode },
    });

    const headerComponents = buildHeaderComponents(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
    );
    const displayComponents = buildDisplayComponents(route, getLayout);

    const children = generateChildren({
        displayComponents,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
        modules,
        fetch,
        pushModal,
        reload,
        onClickConfigurator: onClickConfiguratorApp,
    });

    const title = headerTitle(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
        fiscalYear,
        importId,
    );

    const downloadableQueries = getDownloadableQueries(matchRoutes, route, location, params);

    const { navigation } = generateNavigation({
        modules,
        asideRepositoryNavigationByModule,
        route,
        fetchResults,
        fetchStatus,
        component: AppNewLogic,
        params,
        fiscalYear,
        importId,
        props,
        navData,
        i18n,
        locale,
    });

    const redirect = redirectNotValid(userInformation, locale, false, auth);

    const appRedirect = appRedirectValidations({
        redirect,
        projectId,
        fiscalYear,
        importId,
        fiscalYears,
        appContext,
        match,
        matchParams,
        importIdToUse,
        firstModuleFromProject,
        section,
        fileType,
    });
    if (appRedirect) {
        return appRedirect;
    }

    const { navProps, staticLinks, leftMenuStaticLinks, hasNewNavigation } = buildNavProps(
        buildStaticLinks,
        navigation,
        history,
        matchParams,
        fiscalYear,
        importId,
        i18n,
        menuOrganizationProject,
        projectId,
        cleanPropertiesNestedInMenu,
        locale,
        proxy,
        params,
        userInformation,
        modules,
        viewMode,
    );

    // TODO: Check this
    // TODO: Show no results when have entitiesMenu and no data from entities
    const newMenuTreeList = (staticLinks || []).filter((menuItem) => {
        // if (!params?.entitiesMenu) return false;
        const searchParams = new URLSearchParams(location.search);
        if (!searchParams.has("viewMode")) {
            searchParams.set("viewMode", "source");
        }

        return (
            menuItem?.useInMenuTreeList &&
            menuItem?.viewMode ===
            (searchParams.has("viewMode") ? searchParams.toString().split("=")[1] : "source")
        );
    });
    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries,
        params: {
            ...params,
            fiscalYear,
            importId,
            ...(viewMode && { viewMode }),
        },
        repositoryStatus,
        getTotalExportLines,
        staticLinks,
        styles,
        organizationProjects,
        leftMenuStaticLinks: hasNewNavigation ? leftMenuStaticLinks : [],
        hasHeroBanner
    };

    return (
        LoadingComponent || (
            <GlobalPageLayout
                menuEntries={!hideNewMenu ? newMenuTreeList : []}
                i18n={i18n}
                styles={styles}
                history={history}
                globalLayoutProps={globalLayoutProps}
                modules={modules}
                headerProps={{
                    genericSearchAction: true,
                    reportsAction: true,
                    appNewLogic: true,
                    fiscalYears: fiscalYearsWithImportIds,
                    importIds: appContext === "repository" ? "" : importIdsFromUrlFiscalYear,
                    downloadableQueries,
                    hasSource,
                    hasLegal,
                    title:
                        (title && Array.isArray(title) ? title.join(" ") : title) ||
                        i18n`${params.section || ""}`,
                    totalLinesCsv: getTotalExportLines,
                    headerComponents: headerComponents,
                    onViewModeChange: viewMode ? setViewMode : null,
                    viewMode: viewMode ? viewMode : null,
                    titleBarActions:
                        headerComponents.length > 0
                            ? headerComponents.map((headerComponent, index) => (
                                <headerComponent.Component
                                    key={`titlebar-action-${index}`}
                                    download={downloadAction}
                                    downloadableQueries={downloadableQueries}
                                    locale={locale}
                                    user={userInformation}
                                    items={route?.headerComponentConfigs?.items || []}
                                    label={i18n`${route?.headerComponentConfigs.label}`}
                                    {...headerComponent.props}
                                />
                            ))
                            : undefined,
                }}
                headerComponentsMapper={(downloading) => (headerComponentProperties) => (
                    <headerComponentProperties.Component
                        download={downloadAction}
                        downloading={downloading}
                        downloadableQueries={downloadableQueries}
                        locale={locale}
                        user={userInformation}
                        items={route?.headerComponentConfigs?.items || []}
                        label={i18n`${route?.headerComponentConfigs.label}`}
                        {...headerComponentProperties.props}
                    />
                )}
                fetch={fetch}
                route={route}
                matchParams={matchParams}
                isLoadingContent={(fetch) => isPageLoading(fetch?.status, fetch?.activity)}
            >
                {children}
            </GlobalPageLayout>
        )
    );
};

AppNewLogic.queries = (args, userModulesByProject) => {
    return controllerQueries(args, userModulesByProject);
};

export default connect(
    ({ user, fetch, menu, uploads, auditNote }) => ({
        user,
        fetch,
        menu,
        uploads,
        auditNote,
    }),
    {
        logout,
        pushModal,
        popModal,
        removeModal,
        toggleMenu,
        setLocale,
        pushDialog,
        getFindingDetails,
        setDataFileStatus: setDataFileStatusAction,
        deleteFilePermanently: deleteFilePermanentlyAction,
        deleteInvalidFilePermanently: deleteInvalidFilePermanentlyAction,
        hideFile: hideFileAction,
        downloadFile: downloadFileAction,
        reprocessRepository: reprocessRepositoryAction,
        uploadFiles: uploadAction,
        download: downloadAction,
        submitAuditNote: auditNoteSubmit,
        genericSearchAction,
        reprocessFiles: reprocessFileAction,
        getDownloadTemplateData,
    },
)(withRouter(AppNewLogic));
